import { orderBy } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Dialog from 'components/DialogWrapper/DialogWrapper';

import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import CalendarPlusIcon from 'mdi-material-ui/CalendarPlus';

import Button from 'components/Button/Button';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import TextInput from 'components/TextInput/TextInput';
import useStyles from './RecordDialogLayoutStyles';

import { MenuItem } from '@material-ui/core';
import DateTimePicker from 'components/DateTimePicker/DateTimePicker';
import Loading from 'components/Loading/Loading';
import dayjs from 'dayjs';
import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import ComboBox from 'scenes/ExpenseTracking/components/ComboBox';
import { selectWorkCalendarEmployersArray } from 'scenes/WorkDemo/store/selectors/base-work-calendar.selectors';
import { selectEmployerAllowancesByBusinessId } from 'store/employerAllowances/selectors/employerAllowances.selectors';
import {
  selectEmployerTypeProjects,
  selectSelectedProject,
} from 'store/projects/selectors/projectsSelectors';
import { selectCreateRecordProjectOptions } from 'store/projects/selectors/recordProjectOptions.selectors';
import { closeCreateAbsenceDialog } from 'store/records/actions/records.actions';
import { selectIsCreatingAbsence } from 'store/records/selectors/records-base.selectors';
import { selectIsDialogResourcesLoading } from 'store/records/selectors/records.selectors';
import { createAbsenceForTimeframe } from 'store/records/thunks/absences/create-absence-for-timeframe.thunk';
import { selectProjectResources } from 'store/resources/selectors/resources.selectors';
import { loadResourcesAndResourceEmployersOfProject } from 'store/resources/thunks/loadResourcesAndResourceEmployersOfProject';
import {
  selectLoggedInUserId,
  selectUserActiveEmployerId,
} from 'store/user/selectors.js/user.selectors';
import sortByName from 'utils/sortByName';

export const CreateAbsenceDialog = () => {
  const dispatch = useDispatch();

  const classes = useStyles();
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));

  const { t } = useTranslation('common');

  const selectedProject = useSelector(selectSelectedProject);

  const [fromDate, setFromDate] = useState(dayjs());
  const [toDate, setToDate] = useState(dayjs());
  const [selectedAbsenceType, setSelectedAbsenceType] = useState('SICK_LEAVE');
  const [selectedResourceId, setSelectedResourceId] = useState('');
  const [selectedAbsenceProjectId, setSelectedAbsenceProjectId] = useState(
    selectedProject ? selectedProject.id : ''
  );
  const [description, setDescription] = useState('');

  const projectOptions = useSelector(selectCreateRecordProjectOptions);

  const isResourcesLoading = useSelector(selectIsDialogResourcesLoading);

  const projectResources = useSelector((state) =>
    selectProjectResources(state, selectedAbsenceProjectId)
  );

  const workCalendarEmployers = useSelector(selectWorkCalendarEmployersArray);

  const employerProjects = useSelector(selectEmployerTypeProjects);

  const selectedResource = projectResources.find(
    (resource) => resource.id === selectedResourceId
  );

  const employerAllowances = useSelector((state) =>
    selectEmployerAllowancesByBusinessId(
      state,
      selectedResource?.employerBusinessId || ''
    )
  );

  const isCreatingAbsence = useSelector(selectIsCreatingAbsence);

  const handleSave = () => {
    dispatch(
      createAbsenceForTimeframe({
        absenceType: selectedAbsenceType,
        fromDate,
        toDate,
        projectId: selectedAbsenceProjectId,
        resourceId: selectedResource.id,
        description,
      })
    );
  };

  const closeDialog = () => {
    dispatch(closeCreateAbsenceDialog());
  };

  const selectedEmployer = useSelector(selectUserActiveEmployerId);

  const loggedInUserId = useSelector(selectLoggedInUserId);

  useEffect(() => {
    const loggedInUserResourceFromActiveEmployer = projectResources.find(
      (resource) =>
        resource.user === loggedInUserId &&
        resource.employer &&
        resource.employer === selectedEmployer
    );

    const loggedInUsersResourceWithAnyEmployer = projectResources.find(
      (resource) => resource.user === loggedInUserId && resource.employer
    );
    if (loggedInUserResourceFromActiveEmployer) {
      setSelectedResourceId(loggedInUserResourceFromActiveEmployer.id);
    } else if (loggedInUsersResourceWithAnyEmployer) {
      setSelectedResourceId(loggedInUsersResourceWithAnyEmployer.id);
    }
  }, []);

  const handleProjectChange = (project) => {
    setSelectedAbsenceProjectId(project);
    dispatch(loadResourcesAndResourceEmployersOfProject(project));
  };

  const absenceTypeOptions = Object.values(
    DEFAULT_ADDITIONAL_SALARY_TYPES
  ).filter(
    (salaryType) =>
      salaryType.baseType === 'ABSENCE' &&
      (employerAllowances?.some(
        (allowance) => allowance.salaryType === salaryType.salaryType
      ) ||
        salaryType.salaryType === 'SICK_LEAVE' ||
        salaryType.salaryType === 'HOLIDAY_PAY')
  );

  const isRequiredFields =
    selectedResourceId &&
    selectedAbsenceType &&
    selectedProject &&
    fromDate &&
    toDate;

  return (
    <>
      <Dialog
        aria-labelledby="create-record-dialog"
        classes={{ paper: classes.dialogPaper }}
        disableBackdropClick
        fullScreen={!upSm}
        fullWidth
        maxWidth="sm"
        open={true}
        onClose={closeDialog}
      >
        <DialogHeader
          icon={<CalendarPlusIcon />}
          onClose={closeDialog}
          title={t('calendar.new_absence')}
        />
        <DialogContent className={classes.dialogContent}>
          <Grid container spacing={2} className={classes.gridContainer}>
            <Grid item xs={12} className={classes.gridItem}>
              <ComboBox
                items={projectOptions}
                selected={selectedAbsenceProjectId}
                onChange={(e) => handleProjectChange(e)}
                label={t('project.project')}
                emptyOption={false}
              />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <ComboBox
                items={projectResources
                  .sort((a, b) => sortByName(a, b, 'lastName'))
                  .map((resource) => {
                    const resourceEmployer = [
                      ...workCalendarEmployers,
                      ...employerProjects,
                    ].find(
                      (employerProject) =>
                        employerProject.business_id ===
                        resource.employerBusinessId
                    );

                    if (resourceEmployer && resourceEmployer.name) {
                      return {
                        ...resource,
                        name: `${resourceEmployer.name} / ${resource.lastName} ${resource.firstName}`,
                      };
                    } else {
                      return {
                        ...resource,
                        name: `MyLixani (Ei työnantajaa) / ${resource.lastName} ${resource.firstName}`,
                      };
                    }
                  })}
                selected={selectedResourceId}
                onChange={(value) => setSelectedResourceId(value)}
                label={t('common.person')}
                emptyOption={false}
                disabled={isResourcesLoading}
                loading={isResourcesLoading}
                error={!selectedResource}
              />
            </Grid>
            <Grid item xs={12} className={classes.gridItem}>
              <TextInput
                displayEmpty
                fullWidth
                id="edit-record-sub-type-select"
                label={'Poissaolon tyyppi'}
                MenuProps={{
                  PaperProps: {
                    className: classes.menuPaper,
                  },
                }}
                onChange={(e) => setSelectedAbsenceType(e.target.value)}
                select
                shrink
                value={selectedAbsenceType}
              >
                {orderBy(
                  absenceTypeOptions,
                  [(item) => t(`${item.salaryName}`)],
                  'asc'
                ).map((item) => (
                  <MenuItem
                    className={classes.menuItem}
                    key={item.salaryType}
                    value={item.salaryType}
                  >
                    {t(`${item.salaryName}`)}
                  </MenuItem>
                ))}
              </TextInput>
            </Grid>
            <Grid item xs={6}>
              <DateTimePicker
                style={{ backgroundColor: '#1687af' }}
                type="date"
                value={fromDate}
                onChange={(event) => {
                  setFromDate(event);
                  if (toDate.isBefore(event)) {
                    setToDate(event);
                  }
                }}
                inputProps={{
                  label: t('calendar.start_date'),
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <DateTimePicker
                style={{ backgroundColor: '#1687af' }}
                type="date"
                value={toDate}
                onChange={(event) => {
                  if (!event.isBefore(fromDate)) {
                    setToDate(event);
                  }
                }}
                inputProps={{
                  label: t('calendar.end_date'),
                }}
              />
            </Grid>

            <Grid item xs={12} className={classes.gridItem}>
              <TextInput
                fullWidth
                id="description"
                onChange={(e) => setDescription(e.target.value)}
                label={t('calendar.description')}
                multiline
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Divider />
        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.actionBtn}
            onClick={closeDialog}
            outline
            color="primary"
            disabled={isCreatingAbsence}
          >
            {t('common.cancel')}
          </Button>
          <Button
            className={classes.actionBtn}
            onClick={handleSave}
            color="primary"
            disabled={!isRequiredFields || isCreatingAbsence}
          >
            {!isCreatingAbsence ? t('common.save') : <Loading tiny></Loading>}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
